export enum CSV_UploadStatus {
  Pending = 'PENDING',
  Ready = 'READY',
  Success = 'SUCCESS',
  Failed = 'FAILED',
}

export interface RawCSVResultI {
  rows: Array<{ fields: string[]; rowNumber: number }>
  headerIndex: HeaderIndex
  errors: string[]
}

export type HeaderIndex = Record<string, number>

const LINE_BREAK_REGEX = /\r?\n/

const REQUIRED_HEADERS = ['name', 'device_type', 'facility_name', 'floor_name']

const parseCSVLine = (line: string): string[] => {
  const fields: string[] = []
  let currentField = ''
  let isInsideQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"') {
      if (isInsideQuotes && nextChar === '"') {
        currentField += '"'
        i++
      } else {
        isInsideQuotes = !isInsideQuotes
      }
    } else if (char === ',' && !isInsideQuotes) {
      fields.push(currentField.trim())
      currentField = ''
    } else {
      currentField += char
    }
  }

  fields.push(currentField.trim())
  return fields
}

export const parseDeviceCSVFile = async (
  file: File
): Promise<RawCSVResultI> => {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      try {
        const result = event.target?.result
        if (typeof result !== 'string') {
          return resolve({
            rows: [],
            headerIndex: {},
            errors: ['Failed to read CSV file'],
          })
        }

        const lines = result
          .split(LINE_BREAK_REGEX)
          .filter((line) => line.trim() !== '')

        if (lines.length === 0) {
          return resolve({
            rows: [],
            headerIndex: {},
            errors: ['CSV file is empty'],
          })
        }

        const headerFields = parseCSVLine(lines[0])
        const headerIndex: HeaderIndex = {}
        headerFields.forEach((h, i) => {
          headerIndex[h.toLowerCase().trim()] = i
        })

        const missingHeaders = REQUIRED_HEADERS.filter(
          (h) => headerIndex[h] === undefined
        )
        if (missingHeaders.length > 0) {
          return resolve({
            rows: [],
            headerIndex,
            errors: [`Missing required columns: ${missingHeaders.join(', ')}`],
          })
        }

        const rows: Array<{ fields: string[]; rowNumber: number }> = []
        lines.slice(1).forEach((line, i) => {
          if (line.trim() === '') return
          rows.push({ fields: parseCSVLine(line), rowNumber: i + 2 })
        })

        if (rows.length > 500) {
          return resolve({
            rows: [],
            headerIndex,
            errors: [
              `CSV exceeds the maximum of 500 rows. Found ${rows.length} rows.`,
            ],
          })
        }

        resolve({ rows, headerIndex, errors: [] })
      } catch {
        resolve({
          rows: [],
          headerIndex: {},
          errors: ['Failed to read CSV file'],
        })
      }
    }

    reader.onerror = () => {
      resolve({
        rows: [],
        headerIndex: {},
        errors: ['Failed to read CSV file'],
      })
    }

    reader.readAsText(file)
  })
}
